import { Avatar } from '@amore/ui/components/avatar'
import { Badge } from '@amore/ui/components/badge'
import { Button, buttonStyles } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { Heading, Text } from '@amore/ui/components/typography'
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  CalendarDays,
  Contact,
  FileText,
  FolderOpen,
  Mail,
  MapPin,
  PencilLine,
  Phone,
  Route,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { daysBetween, initials, plural } from '@/lib/format'
import { useCanEdit, useWedding } from '@/mock/store'
import type { Supplier } from '@/mock/types'
import { type Venue, venueIdentity } from '@/mock/venues'
import { type VenueEditSection, venueOperationFields } from './model'

const dayMonth = (iso: string) => {
  const date = new Date(`${iso}T12:00:00`)
  return `${date.getDate()}. ${date.getMonth() + 1}.`
}

/** "11.–13. 6." for a stay within one month, "30. 6.–2. 7." across two. */
function availability(from: string, to: string) {
  if (!from) return null
  if (!to || to === from) return dayMonth(from)
  return from.slice(0, 7) === to.slice(0, 7)
    ? `${Number(from.slice(8))}.–${dayMonth(to)}`
    : `${dayMonth(from)}–${dayMonth(to)}`
}

export function PlacePlan({
  venue,
  onEdit,
}: {
  venue: Venue
  onEdit: (section: VenueEditSection) => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const identity = venueIdentity(venue, data)
  const editAction = (section: VenueEditSection, label: string) =>
    canEdit ? (
      <Button
        id={`edit-venue-${venue.id}-${section}`}
        appearance="ghost"
        tone="neutral"
        size="sm"
        aria-label={label}
        onClick={() => onEdit(section)}
      >
        <PencilLine aria-hidden="true" className="size-3.5" />
        Upravit
      </Button>
    ) : null
  const dates = availability(venue.availableFrom, venue.availableTo)
  const days =
    venue.availableFrom && venue.availableTo
      ? daysBetween(venue.availableFrom, venue.availableTo) + 1
      : null
  const operationFields = venueOperationFields.filter(
    (field) => field.key !== 'accommodation' || venue.uses.includes('Ubytování'),
  )
  return (
    <section aria-label={`Plán místa ${identity.name}`} className="space-y-6">
      <Card padding="none" className="overflow-hidden">
        <div className="space-y-3 bg-panel-accent px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Heading as="h2" size="md">
                {identity.name}
              </Heading>
              <Text tone="muted" className="mt-1 flex items-center gap-1.5">
                <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
                {identity.private ? 'Soukromé místo · ' : ''}
                {identity.address || 'Adresa zatím není uvedená.'}
              </Text>
            </div>
            {editAction('identity', 'Upravit údaje místa')}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex flex-wrap gap-1.5">
              {venue.uses.length ? (
                venue.uses.map((use) => (
                  <Badge key={use} tone="accent">
                    {use}
                  </Badge>
                ))
              ) : (
                <Badge tone="accent">Využití zatím otevřené</Badge>
              )}
            </div>
            <Text tone="muted" className="flex items-center gap-1.5">
              <CalendarDays className="size-3.5 shrink-0" aria-hidden="true" />
              {dates
                ? `K dispozici ${dates}${days ? ` · ${days} ${plural(days, 'den', 'dny', 'dní')}` : ''}`
                : 'Termín zatím nedomluvený'}
              {venue.handoverTime ? ` · předání ${venue.handoverTime}` : ''}
            </Text>
          </div>
        </div>
      </Card>
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <PlaceCard
            icon={<CalendarDays className="size-4 text-action" aria-hidden="true" />}
            title="Domluva s místem"
            action={editAction('agreement', 'Upravit domluvu s místem')}
            rows={[
              { label: 'Předání místa', value: venue.handover },
              { label: 'Co místo zajistí', value: venue.included },
              { label: 'Pravidla a omezení', value: venue.restrictions },
            ]}
          />
          <PlaceCard
            icon={<Route className="size-4 text-action" aria-hidden="true" />}
            title="Příjezd a zázemí"
            action={editAction('operations', 'Upravit příjezd a zázemí')}
            rows={operationFields.map((field) => ({ label: field.label, value: venue[field.key] }))}
          />
        </div>
        <div className="space-y-6">
          {identity.supplier ? <PlaceContacts supplier={identity.supplier} /> : null}
          <Card padding="none" className="overflow-hidden">
            <CardHeader appearance="strip">
              <div className="flex items-center gap-2.5">
                <FolderOpen className="size-4 text-action" aria-hidden="true" />
                <CardTitle as="h2">Soubory a poznámky</CardTitle>
              </div>
              {editAction('materials', 'Upravit soubory a poznámky')}
            </CardHeader>
            <div className="space-y-2 p-5">
              <p
                className={
                  venue.notes ? 'text-sm whitespace-pre-line' : 'text-sm text-muted-foreground'
                }
              >
                {venue.notes ||
                  (canEdit
                    ? 'Nápady, kontakty a další domluvy k místu, ať je máte po ruce.'
                    : 'Zatím bez poznámek.')}
              </p>
            </div>
            <div className="divide-y divide-border border-t border-border px-5">
              {venue.documents.length ? (
                venue.documents.map(({ id, file }) => (
                  <div key={id} className="flex items-center gap-3 py-3">
                    <FileText
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="min-w-0 flex-1 truncate text-sm">{file.name}</p>
                    <Text variant="small" tone="muted">
                      {Math.max(1, Math.round(file.size / 1024))} kB
                    </Text>
                  </div>
                ))
              ) : (
                <Text variant="small" tone="muted" className="py-4">
                  Dohody, půdorys nebo pokyny pro dodavatele nahrajete v úpravě.
                </Text>
              )}
            </div>
          </Card>
        </div>
      </div>
    </section>
  )
}

function PlaceCard({
  icon,
  title,
  action,
  rows,
}: {
  icon: ReactNode
  title: string
  action: ReactNode
  rows: { label: string; value: string }[]
}) {
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          {icon}
          <CardTitle as="h2">{title}</CardTitle>
        </div>
        {action}
      </CardHeader>
      <div className="divide-y divide-border px-5">
        {rows.map((row) => (
          <div key={row.label} className="space-y-1 py-4">
            <Text variant="small" tone="muted">
              {row.label}
            </Text>
            {row.value ? (
              <p className="text-sm whitespace-pre-line">{row.value}</p>
            ) : (
              <Text tone="muted">Zatím nedoplněno</Text>
            )}
          </div>
        ))}
      </div>
    </Card>
  )
}

/** The venue's contacts, read-only: My suppliers is where they are kept. */
function PlaceContacts({ supplier }: { supplier: Supplier }) {
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <Contact className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Kontakty</CardTitle>
        </div>
      </CardHeader>
      {supplier.contacts.length ? (
        <div className="divide-y divide-border px-5">
          {supplier.contacts.map((contact) => (
            <div key={contact.id} className="flex items-center gap-3 py-4">
              <Avatar alt="" fallback={initials(contact.name.replace(/\b\p{L}+\.\s*/gu, ''))} />
              <div className="min-w-0 flex-1 space-y-1">
                {contact.role ? (
                  <Text variant="small" tone="muted">
                    {contact.role}
                  </Text>
                ) : null}
                <p className="text-sm font-semibold">{contact.name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {contact.phone ? (
                    <a
                      href={`tel:${contact.phone.replaceAll(' ', '')}`}
                      className="inline-flex items-center gap-1.5 text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                    >
                      <Phone className="size-3.5" aria-hidden="true" />
                      {contact.phone}
                    </a>
                  ) : null}
                  {contact.email ? (
                    <a
                      href={`mailto:${contact.email}`}
                      className="inline-flex min-w-0 items-center gap-1.5 text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                    >
                      <Mail className="size-3.5 shrink-0" aria-hidden="true" />
                      <span className="truncate">{contact.email}</span>
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Text tone="muted" className="p-5">
          U dodavatele zatím nemáte žádný kontakt.
        </Text>
      )}
      <div className="flex justify-end border-t border-border px-5 py-2">
        <Link
          to={`/suppliers/${supplier.id}`}
          className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
        >
          V Mých dodavatelích
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}
